<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-item-list">
        <i class="far fa-list"></i> NCR Item List
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-item-list">
        <div class="row">
          <div class="col-12">
            <ReportFilterPopover
              class="float-right"
              headerTitle="NCR List Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyOrderListFilter"
              @reset="resetOrderListFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingListFilter.startDate"
                  :endDate="editingListFilter.endDate"
                  @updateStartDate="editingListFilter.startDate = $event"
                  @updateEndDate="editingListFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedListFilter.startDate | formatDate}} - {{appliedListFilter.endDate | formatDate}}
                </div>
                
              </template>
            </ReportFilterPopover>
            <h3 class="mb-0">Non-Chargeable Rework Order List</h3>
            <small>All prices ex-{{taxName}}</small>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12">
            <StaticDataTable class="static-table-list" :striped="false" smallRows>
              <template v-slot:header>
                <tr>
                  <th>NCR</th>
                  <th>Details</th>
                  <th>Date</th>
                  <th>Order</th>
                  <th>Created / Processed By</th>
                  <th>Request Notes</th>
                  <th>Response Notes</th>
                  <th>Quantity</th>
                  <th>Labels</th>
                  <th>NCR Cost</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="order in ncrOrderListData">
                  <tr :key="`order-info-${order.id}`" class="static-datatable-striped">
                    <td class="font-weight-bold">
                      <LinkOrder :id="order.id" :name="order.sequenceNumber" />
                    </td>
                    <td>
                      <LinkContact :id="order.contact.id" :name="order.contact.name" />
                    </td>
                    <td>{{order.dateOrderCreated | formatDate}}</td>
                    <td>
                      <LinkOrder :id="order.originalOrder.id" :name="order.originalOrder.sequenceNumber" />
                    </td>
                    <td>
                      <LinkUser v-if="order.requestUser" :id="order.requestUser.id" :name="order.requestUser.name" />
                    </td>
                    <td> </td>
                    <td> </td>
                    <td> </td>
                    <td> </td>
                    <td class="font-weight-bold">{{order.ncrTotalCost | formatCurrency}}</td>
                  </tr>
                  <template v-for="orderItem in order.orderItems">
                    <tr :key="`order-item-${orderItem.id}`">
                      <td>
                        <i class="fas fa-caret-right mr-1 ml-2"></i><LinkStock :id="orderItem.stock.id" :name="orderItem.stock.code" />
                      </td>
                      <td>
                        <LinkStock :id="orderItem.stock.id" :name="orderItem.stock.name" />
                      </td>
                      <td>-</td>
                      <td>{{orderItem.stockPriceGroup ? `${orderItem.stockPriceGroup.name} - ` : ''}}{{orderItem.glassHeight}} x {{orderItem.glassWidth}}</td>
                      <td>
                        <LinkUser v-if="orderItem.user" :id="orderItem.user.id" :name="orderItem.user.name" />
                      </td>
                      <td>{{orderItem.ncrRequestReason}}</td>
                      <td>{{orderItem.approvalInfo}}</td>
                      <td>
                        <template v-if="orderItem.acceptedQty > 0"><i class="far fa-check-circle status-approved mr-2"></i>{{orderItem.acceptedQty}} Accepted <br /></template>
                        <template v-if="orderItem.acceptedQty < orderItem.quantity"><i class="far fa-times-circle status-denied mr-2"></i>{{orderItem.quantity - orderItem.acceptedQty}} Rejected</template>
                      </td>
                      <td>
                        <LabelTags :labelList="orderItem.labels" />
                      </td>
                      <td>{{orderItem.totalValue | formatCurrency}}</td>
                    </tr>
                    <tr :key="`order-item-${orderItem.id}-spacer`">
                      <td colspan="10" class="py-0">&nbsp;</td>
                    </tr>
                  </template>
                </template>
                <tr class="border-top font-weight-bold">
                  <td>Totals:</td>
                  <td>{{ncrOrderListData.length}} NCR order{{ncrOrderListData.length | displayCountPlural}}</td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                  <td>{{totalAcceptedNCRItems}} Item{{totalAcceptedNCRItems | displayCountPlural}}</td>
                  <td> </td>
                  <td>{{totalNCRItemValue | formatCurrency}}</td>
                </tr>
              </template>
              <template v-slot:empty v-if="ncrOrderListData.length == 0">
                No NCR orders found for the date range
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'

import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

export default {
  name: 'NCRValue',
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  data() {
    return {
      ncrValueSummary: {},
      ncrCauseValueData: [],
      ncrCauseCountData: [],
      ncrOrderListData: [],

      ncrCauseValueChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          yAxes: [{
            display: true,
            scaleLabel: {
              display: true,
              labelString: 'NCR Causes'
            }
          }],
          xAxes: [{
            gridLines: {
              color: 'rgba(0,0,0,0)'
            },
            ticks: {
              maxTicksLimit: 20,
              callback: function(value, index, values) {
                return Utils.formatCurrency(value, 0)
              }
            },
            offset: true
          }]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              let totalValue = (data.datasets[tooltipItem.datasetIndex].data || []).reduce((curValue, value) => curValue + parseFloat(value), 0)

              var label = Utils.formatCurrency(parseFloat(tooltipItem.xLabel))
              if(totalValue > 0)
                label += ' (' + Utils.formatPercent((parseFloat(tooltipItem.xLabel) / totalValue) * 100.0) + ')'
              return label
            }
          }
        }
      },
      ncrCauseCountChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          yAxes: [{
            display: true,
            scaleLabel: {
              display: true,
              labelString: 'NCR Causes'
            }
          }],
          xAxes: [{
            gridLines: {
              color: 'rgba(0,0,0,0)'
            },
            ticks: {
              maxTicksLimit: 20
            },
            offset: true
          }]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              let totalCount = (data.datasets[tooltipItem.datasetIndex].data || []).reduce((curValue, value) => curValue + parseInt(value), 0)

              var label = parseInt(tooltipItem.xLabel)
              if(totalCount > 0)
                label += ' (' + Utils.formatPercent((parseInt(tooltipItem.xLabel) / totalCount) * 100.0) + ')'
              return label
            }
          }
        }
      },

      defaultSummaryFilter: {    
        startDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('month').endOf('day').toDate().toISOString()
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      defaultListFilter: {    
        startDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('month').endOf('day').toDate().toISOString()
      },
      editingListFilter: {},
      appliedListFilter: {}
    }
  },
  computed: {
    ...mapState({
      taxName: state => state.account.salesTaxRate.name
    }),
    hasOrders() {
      return this.ncrValueSummary.orderTotalCount > 0
    },
    totalAcceptedNCRItems() {
      return this.ncrOrderListData.reduce((curCount, order) => {
        return curCount + order.orderItems.reduce((curAcceptedCount, orderItem) => curAcceptedCount + parseInt(orderItem.acceptedQty), 0)
      }, 0)
    },
    totalNCRItemValue() {
      return this.ncrOrderListData.reduce((curValue, order) => {
        return curValue + order.orderItems.reduce((curItemValue, orderItem) => curItemValue + parseFloat(orderItem.totalValue), 0)
      }, 0)
    },
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.editingListFilter = _.clone(this.defaultListFilter)
    this.appliedListFilter = _.clone(this.defaultListFilter)

    this.requestSummary()
    this.requestOrderList()

    this.subscribeEndpoint('/orders', () => {
      this.requestSummary()
      this.requestOrderList()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestSummary() {

      let startDate = this.appliedSummaryFilter.startDate
      let endDate = this.appliedSummaryFilter.endDate

      this.$apiService.analyticsService.getNCRValueSummary({ startDate: startDate, endDate: endDate })
      .then(response => {
        this.ncrValueSummary = response.data

        this.ncrCauseValueData = {
          labels: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.labelName),
          datasets: [{
            data: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.totalItemValue),
            backgroundColor: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.labelColour)
          }]
        }
        this.ncrCauseCountData = {
          labels: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.labelName),
          datasets: [{
            data: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.itemCount),
            backgroundColor: this.ncrValueSummary.ncrCauseList.map(causeInfo => causeInfo.labelColour)
          }]
        }
      })
    },
    requestOrderList() {
      let startDate = this.appliedListFilter.startDate
      let endDate = this.appliedListFilter.endDate

      this.$apiService.analyticsService.getNCROrders({ startDate: startDate, endDate: endDate })
      .then(response => {
        this.ncrOrderListData = response.data
      })      
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestSummary()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestSummary()
    },
    applyOrderListFilter() {
      this.appliedListFilter = _.clone(this.editingListFilter)

      this.requestOrderList()
    },
    resetOrderListFilter() {
      this.editingListFilter = _.clone(this.defaultListFilter)
      this.appliedListFilter = _.clone(this.defaultListFilter)

      this.requestOrderList()
    },
  }
}
</script>
